import { useCallback, useEffect, useRef, useState } from 'react'
import { isRequestCanceled } from '../../services/UrgencySummaryController'

/** `loading`: todavía no llegó (o no se pidió); `failed`: la consulta falló; `ready`: hay datos. */
export type SectionStatus = 'loading' | 'ready' | 'failed'

export interface SectionState<T> {
  status: SectionStatus
  /** El resultado de la consulta actual; null mientras carga o si falló. */
  data: T | null
  /** El último resultado de una consulta anterior, para mostrarlo atenuado mientras llega el nuevo; null si ya llegó el actual. */
  stale: T | null
  /** «Reintentar»: vuelve a pedir lo mismo sin mirar el caché. */
  retry: () => void
}

/**
 * Una consulta con su propio estado de carga y error, para las secciones de la pantalla y del panel del día que fallan por
 * separado (el resto sigue funcionando). `key` identifica lo que se pide (período, filtros y sección): cada resultado queda
 * en un caché por `key`, así que volver a una consulta ya hecha no la repite, y la que deja de verse se cancela con su
 * `AbortController`. Con `key` null no consulta (sección oculta o pestaña que todavía no se abrió).
 */
export default function useSectionData<T>(key: string | null, load: (signal: AbortSignal) => Promise<T>): SectionState<T> {
  const loadRef = useRef(load)
  loadRef.current = load
  const cache = useRef(new Map<string, T>())
  const lastReady = useRef<T | null>(null)
  const [result, setResult] = useState<{ key: string; data: T | null } | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    if (key === null) return undefined
    const cached = cache.current.get(key)
    if (cached !== undefined) {
      setResult({ key, data: cached })
      return undefined
    }
    const controller = new AbortController()
    loadRef.current(controller.signal)
      .then((data) => {
        cache.current.set(key, data)
        if (!controller.signal.aborted) setResult({ key, data })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isRequestCanceled(error)) return
        setResult({ key, data: null })
      })
    return () => controller.abort()
  }, [key, reload])

  const retry = useCallback(() => {
    if (key === null) return
    cache.current.delete(key)
    setResult(null)
    setReload((count) => count + 1)
  }, [key])

  // Lo que ya está en el caché se ve en el mismo renderizado (sin un parpadeo de «Cargando» hasta que corra el efecto)
  const cachedNow = key === null ? undefined : cache.current.get(key)
  const current = key !== null && result?.key === key ? result : cachedNow !== undefined ? { key, data: cachedNow } : null
  if (current?.data) lastReady.current = current.data
  return {
    status: current === null ? 'loading' : current.data ? 'ready' : 'failed',
    data: current?.data ?? null,
    stale: current === null ? lastReady.current : null,
    retry,
  }
}
